import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Award, CheckCircle, Briefcase, Users, Trophy, FileText, Upload, X, Sparkles, Clock, Target, BookOpen, AlertCircle, Star, Shield } from 'lucide-react';
import api from '../services/api';

function InternshipEnrollment() {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    legalFullName: '',
    dateOfBirth: '',
    phone: '',
    officialEmail: '',
    currentAddress: '',
    permanentAddress: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    highestQualification: '',
    collegeName: '',
    graduationYear: '',
    studentId: '',
    // File uploads
    resume: null,
    photo: null,
    aadharCard: null,
    panCard: null,
    collegeId: null,
    marksheets: null,
    addressProof: null
  });

  useEffect(() => {
    const fetchEnrollment = async () => {
      try {
        const response = await api.get(`/enrollments/${enrollmentId}`);
        setEnrollment(response.data);
        
        if (response.data.enrollmentStatus !== 'completed') {
          setError('Complete all sections to proceed with internship enrollment');
        }
      } catch (error) {
        console.error('Error fetching enrollment:', error);
        setError('Failed to load enrollment details');
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollment();
  }, [enrollmentId]);

  const getAssignedProject = (category) => {
    const projects = {
      'A': {
        name: 'Medivaulta',
        subtitle: 'Hospital Management System',
        description: 'A comprehensive healthcare platform with patient management, appointment scheduling, medical records, billing, and analytics.',
        difficulty: 'Advanced',
        duration: '8-10 weeks',
        icon: '🏥',
        color: 'from-emerald-500 to-teal-600'
      },
      'B': {
        name: 'B2B Connect',
        subtitle: 'Business-to-Business Platform',
        description: 'An enterprise-level platform for B2B transactions, vendor management, invoice processing, and supply chain coordination.',
        difficulty: 'Intermediate',
        duration: '6-8 weeks',
        icon: '💼',
        color: 'from-blue-500 to-indigo-600'
      },
      'C': {
        name: 'TaskFlow Pro',
        subtitle: 'Task Manager System',
        description: 'A robust task management application with team collaboration, project tracking, deadlines, and productivity analytics.',
        difficulty: 'Beginner-Friendly',
        duration: '4-6 weeks',
        icon: '✓',
        color: 'from-purple-500 to-pink-600'
      }
    };

    return projects[category] || projects['C'];
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData(prev => ({ ...prev, [name]: files[0] }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = ['legalFullName', 'dateOfBirth', 'phone', 'officialEmail', 'currentAddress', 'emergencyContactName', 'emergencyContactPhone', 'highestQualification', 'collegeName', 'graduationYear'];
    const requiredFiles = ['resume', 'photo', 'aadharCard', 'collegeId'];
    
    for (let field of requiredFields) {
      if (!formData[field]) {
        alert(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return;
      }
    }
    
    for (let file of requiredFiles) {
      if (!formData[file]) {
        alert(`Please upload ${file.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return;
      }
    }

    try {
      setLoading(true);
      
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add text fields
      submitData.append('enrollmentId', enrollmentId);
      submitData.append('internshipType', selectedOption.type === 'With Mentorship' ? 'with-mentor' : 'without-mentor');
      submitData.append('price', selectedOption.price);
      submitData.append('assignedProject', JSON.stringify(selectedOption.project));
      submitData.append('legalFullName', formData.legalFullName);
      submitData.append('dateOfBirth', formData.dateOfBirth);
      submitData.append('phone', formData.phone);
      submitData.append('officialEmail', formData.officialEmail);
      submitData.append('emergencyContactName', formData.emergencyContactName);
      submitData.append('emergencyContactPhone', formData.emergencyContactPhone);
      submitData.append('currentAddress', formData.currentAddress);
      submitData.append('permanentAddress', formData.permanentAddress || formData.currentAddress);
      submitData.append('highestQualification', formData.highestQualification);
      submitData.append('collegeName', formData.collegeName);
      submitData.append('graduationYear', formData.graduationYear);
      submitData.append('studentId', formData.studentId || '');
      
      // Add files
      if (formData.resume) submitData.append('resume', formData.resume);
      if (formData.photo) submitData.append('photo', formData.photo);
      if (formData.aadharCard) submitData.append('aadharCard', formData.aadharCard);
      if (formData.panCard) submitData.append('panCard', formData.panCard);
      if (formData.collegeId) submitData.append('collegeId', formData.collegeId);
      if (formData.marksheets) submitData.append('marksheets', formData.marksheets);
      if (formData.addressProof) submitData.append('addressProof', formData.addressProof);

      // Submit to backend
      const response = await api.post('/internship/enroll', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      alert(`✅ ${response.data.message}\n\n📧 Please check your email (${formData.officialEmail}) for payment details and next steps.`);
      setShowModal(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Enrollment error:', error);
      alert(error.response?.data?.message || 'Failed to submit enrollment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading enrollment details...</p>
        </div>
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <AlertCircle className="w-6 h-6 text-red-600 mb-2" />
          <p className="text-red-800 font-medium">Error</p>
          <p className="text-red-700">Enrollment not found</p>
        </div>
      </div>
    );
  }

  const userCategory = enrollment.user?.category || 'C';
  const assignedProject = getAssignedProject(userCategory);

  const internshipOptions = [
    {
      type: 'Without Mentorship',
      price: 499,
      features: [
        'Self-paced project development',
        'Access to project documentation',
        'Code review upon completion',
        'Experience certificate after completion',
        'Community forum support',
        'Resource library access'
      ],
      project: assignedProject,
      ideal: 'For self-motivated learners',
      badge: 'Budget-Friendly'
    },
    {
      type: 'With Mentorship',
      price: 2999,
      features: [
        '✨ Dedicated mentor assignment',
        '✨ Weekly 1-on-1 mentorship sessions',
        '✨ Live project guidance & code reviews',
        '✨ Career guidance & resume building',
        '✨ Interview preparation support',
        '✨ Priority doubt resolution',
        'Full project development assistance',
        'Industry-standard best practices training',
        'Experience certificate after completion',
        'LinkedIn recommendation from mentor',
        'Project portfolio development',
        'Direct mentor WhatsApp/Slack access'
      ],
      project: assignedProject,
      ideal: 'For maximum career growth',
      badge: 'Most Popular',
      recommended: true
    }
  ];

  return (
    <div style={{marginTop: "73px"}} className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Celebration Header */}
      <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mb-6 animate-bounce">
            <Trophy className="w-12 h-12 text-yellow-300" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            🎉 Congratulations!
          </h1>
          <p className="text-2xl md:text-3xl font-semibold text-green-100 mb-2">
            You've Completed the Evaluation!
          </p>
          <p className="text-lg text-green-50 max-w-2xl mx-auto">
            You've successfully completed all sections and assessments. Now it's time to apply your skills in a real-world project.
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-3xl font-bold">{enrollment.overallProgress}%</div>
              <div className="text-sm text-green-50 mt-1">Overall Progress</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-3xl font-bold">{enrollment.sectionsProgress?.length || 0}</div>
              <div className="text-sm text-green-50 mt-1">Sections Completed</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-3xl font-bold text-yellow-300">{userCategory}</div>
              <div className="text-sm text-green-50 mt-1">Category Achieved</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-3xl font-bold">✓</div>
              <div className="text-sm text-green-50 mt-1">Ready for Internship</div>
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Project Section */}
      <div className="max-w-6xl mx-auto px-4 -mt-8 mb-12">
        <div className={`bg-gradient-to-r ${assignedProject.color} text-white rounded-2xl shadow-2xl overflow-hidden`}>
          <div className="p-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
                  <Star className="w-4 h-4" />
                  <span className="text-sm font-semibold">Your Assigned Project</span>
                </div>
                <h2 className="text-4xl font-bold mb-2 flex items-center gap-3">
                  <span className="text-5xl">{assignedProject.icon}</span>
                  {assignedProject.name}
                </h2>
                <p className="text-xl text-white/90 mb-4">{assignedProject.subtitle}</p>
              </div>
            </div>
            
            <p className="text-lg text-white/90 mb-6 leading-relaxed">
              {assignedProject.description}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <Target className="w-6 h-6 mb-2" />
                <div className="text-sm text-white/80">Difficulty</div>
                <div className="text-lg font-bold">{assignedProject.difficulty}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <Clock className="w-6 h-6 mb-2" />
                <div className="text-sm text-white/80">Duration</div>
                <div className="text-lg font-bold">{assignedProject.duration}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <BookOpen className="w-6 h-6 mb-2" />
                <div className="text-sm text-white/80">Category Match</div>
                <div className="text-lg font-bold">Category {userCategory}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Internship Options */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Choose Your Internship Path
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Select the option that best fits your learning style and career goals
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {internshipOptions.map((option, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                option.recommended ? 'border-4 border-gradient-to-r from-yellow-400 to-orange-500 ring-4 ring-yellow-200' : 'border border-slate-200'
              }`}
            >
              {option.recommended && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-slate-900 px-6 py-2 font-bold text-sm shadow-lg transform rotate-0 rounded-bl-2xl">
                  ⭐ {option.badge}
                </div>
              )}
              
              {!option.recommended && option.badge && (
                <div className="absolute top-4 right-4 bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs font-semibold">
                  {option.badge}
                </div>
              )}

              <div className={`p-8 ${option.recommended ? 'bg-gradient-to-br from-yellow-50 to-orange-50' : ''}`}>
                <div className="flex items-center gap-3 mb-4">
                  {option.recommended ? (
                    <Users className="w-8 h-8 text-orange-600" />
                  ) : (
                    <Briefcase className="w-8 h-8 text-slate-600" />
                  )}
                  <h3 className="text-2xl font-bold text-slate-900">
                    {option.type}
                  </h3>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-slate-900">₹{option.price.toLocaleString()}</span>
                    <span className="text-slate-600">/one-time</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-2 italic">{option.ideal}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        feature.startsWith('✨') ? 'text-orange-500' : 'text-green-600'
                      }`} />
                      <span className={`text-slate-700 ${feature.startsWith('✨') ? 'font-semibold' : ''}`}>
                        {feature.replace('✨ ', '')}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleOptionSelect(option)}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl ${
                    option.recommended
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {option.recommended ? '🚀 Start with Mentorship' : 'Continue Without Mentor'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Important Note */}
        <div className="mt-12 bg-amber-50 border-l-4 border-amber-400 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-bold text-amber-900 mb-2">Important Notice</h4>
              <p className="text-amber-800 text-sm leading-relaxed">
                Please note that if you choose either internship option and discontinue before completion, an experience certificate cannot be provided. 
                We encourage commitment to the full duration to maximize your learning and receive official certification.
              </p>
            </div>
          </div>
        </div>

        {/* Payment Email Notice */}
        <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-bold text-blue-900 mb-2">📧 Next Steps After Enrollment</h4>
              <p className="text-blue-800 text-sm leading-relaxed mb-2">
                After submitting your enrollment form, our team will review your documents within 24-48 hours.
              </p>
              <p className="text-blue-800 text-sm leading-relaxed font-semibold">
                ✉️ You will receive detailed payment information, project guidelines, and mentor details via email. 
                Please ensure you provide a valid email address.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Enrollment Modal */}
      {showModal && selectedOption && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-1">Internship Enrollment Form</h3>
                <p className="text-slate-300">
                  {selectedOption.type} - ₹{selectedOption.price.toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-8">
              <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Legal Documentation Required</h4>
                    <p className="text-sm text-blue-800">
                      Please provide accurate information and upload valid documents. All information will be verified for legal compliance and certificate issuance.
                    </p>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="mb-8">
                <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Personal Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Legal Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="legalFullName"
                      value={formData.legalFullName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="As per government documents"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="+91 XXXXXXXXXX"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Official Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="officialEmail"
                      value={formData.officialEmail}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Emergency Contact Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="emergencyContactName"
                      value={formData.emergencyContactName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="Parent/Guardian name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Emergency Contact Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="emergencyContactPhone"
                      value={formData.emergencyContactPhone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="+91 XXXXXXXXXX"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="mb-8">
                <h4 className="text-lg font-bold text-slate-900 mb-4">Address Details</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Current Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="currentAddress"
                      value={formData.currentAddress}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="Full address with pincode"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Permanent Address
                    </label>
                    <textarea
                      name="permanentAddress"
                      value={formData.permanentAddress}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="Leave blank if same as current"
                    />
                  </div>
                </div>
              </div>

              {/* Educational Information */}
              <div className="mb-8">
                <h4 className="text-lg font-bold text-slate-900 mb-4">Educational Information</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Highest Qualification <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="highestQualification"
                      value={formData.highestQualification}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      required
                    >
                      <option value="">Select qualification</option>
                      <option value="High School">High School</option>
                      <option value="Diploma">Diploma</option>
                      <option value="Undergraduate">Undergraduate (Pursuing)</option>
                      <option value="Graduate">Graduate</option>
                      <option value="Postgraduate">Postgraduate</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      College/University Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="collegeName"
                      value={formData.collegeName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="Full institution name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Graduation Year <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="graduationYear"
                      value={formData.graduationYear}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="YYYY"
                      min="2000"
                      max="2030"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Student/Roll Number
                    </label>
                    <input
                      type="text"
                      name="studentId"
                      value={formData.studentId}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                      placeholder="College student ID"
                    />
                  </div>
                </div>
              </div>

              {/* Document Uploads */}
              <div className="mb-8">
                <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Document Uploads
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Resume */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-400 transition-colors">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Resume/CV <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      name="resume"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx"
                      className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">PDF or DOC (Max 5MB)</p>
                  </div>

                  {/* Photo */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-400 transition-colors">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Passport Photo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      name="photo"
                      onChange={handleFileChange}
                      accept="image/*"
                      className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">JPG or PNG (Max 2MB)</p>
                  </div>

                  {/* Aadhar Card */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-400 transition-colors">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Aadhar Card <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      name="aadharCard"
                      onChange={handleFileChange}
                      accept=".pdf,image/*"
                      className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">Scanned copy (Front & Back)</p>
                  </div>

                  {/* PAN Card */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-400 transition-colors">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      PAN Card (Optional)
                    </label>
                    <input
                      type="file"
                      name="panCard"
                      onChange={handleFileChange}
                      accept=".pdf,image/*"
                      className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    />
                    <p className="text-xs text-slate-500 mt-1">If available</p>
                  </div>

                  {/* College ID */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-400 transition-colors">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      College ID Card <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      name="collegeId"
                      onChange={handleFileChange}
                      accept=".pdf,image/*"
                      className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">Valid student ID</p>
                  </div>

                  {/* Marksheets */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-400 transition-colors">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Latest Marksheet/Transcript
                    </label>
                    <input
                      type="file"
                      name="marksheets"
                      onChange={handleFileChange}
                      accept=".pdf"
                      className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    />
                    <p className="text-xs text-slate-500 mt-1">Latest semester/year</p>
                  </div>

                  {/* Address Proof */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-slate-400 transition-colors">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Address Proof
                    </label>
                    <input
                      type="file"
                      name="addressProof"
                      onChange={handleFileChange}
                      accept=".pdf,image/*"
                      className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    />
                    <p className="text-xs text-slate-500 mt-1">Utility bill/Bank statement</p>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="mb-8 bg-slate-50 border border-slate-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms"
                    className="mt-1 w-4 h-4"
                    required
                  />
                  <label htmlFor="terms" className="text-sm text-slate-700">
                    I hereby declare that all information provided is true and accurate. I understand that:
                    <ul className="list-disc ml-5 mt-2 space-y-1">
                      <li>Providing false information may result in internship termination</li>
                      <li>Experience certificate will only be issued upon successful completion</li>
                      <li>Discontinuation before completion will not result in certificate issuance</li>
                      <li>All documents submitted will be verified for authenticity</li>
                      <li>The internship fee is non-refundable once enrolled</li>
                    </ul>
                  </label>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-4 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl font-semibold hover:from-slate-800 hover:to-slate-700 transition-all shadow-lg"
                >
                  Submit Enrollment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default InternshipEnrollment;
